import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { WIDTH_DATA, MATERIAL_DATA } from '../data/vdot.js';
import { fmt, Pill, SectionHeader, CitationBlock } from '../components/primitives.jsx';

export default function Inventory() {
  const nav = useNavigate();
  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <Pill variant="teal">VDOT inventory</Pill>
            <h1 className="text-3xl text-[#0a0a0a] mt-3 font-serif-display">Data quality &amp; methodology</h1>
            <p className="text-sm text-stone-600 mt-2 max-w-2xl">
              VDOT's 2019 inventory uses video-log + desktop review per VTRC Report 21-R18. AtlasPath augments
              this with calibrated LiDAR (±0.5pp slope, ±0.5in width per CLAUDE.md Part 15) and exposes the
              data quality flags VDOT itself records.
            </p>
          </div>
          <button onClick={() => nav('/dashboard')} className="text-xs text-[#1F7A8C] hover:underline">← Back to dashboard</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 p-6">
          <SectionHeader sub="PROWAG R304.5.1.1: minimum 48 inches">Width compliance</SectionHeader>
          <div className="space-y-3">
            {WIDTH_DATA.map((w) => {
              const pct = (w.count / 88717 * 100).toFixed(1);
              return (
                <div key={w.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={w.fail ? 'text-[#7A1F2B] font-medium' : 'text-stone-700'}>{w.name}</span>
                    <span className="font-mono text-stone-700">{fmt(w.count)} <span className="text-stone-400">({pct}%)</span></span>
                  </div>
                  <div className="h-2 bg-stone-100 overflow-hidden">
                    <div className={`h-full ${w.fail ? 'bg-[#7A1F2B]' : 'bg-[#1F7A8C]'}`} style={{ width: pct + '%' }}></div>
                  </div>
                </div>
              );
            })}
          </div>
          <CitationBlock source="PROWAG R304.5.1.1 (88 FR 53604); VDOT width records (76,640 of 82,438 measured)." />
        </div>

        <div className="bg-white border border-stone-200 p-6">
          <SectionHeader sub="VDOT-recorded condition">Material condition</SectionHeader>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={MATERIAL_DATA} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis dataKey="name" stroke="#78716c" style={{ fontSize: '11px' }} />
              <YAxis stroke="#78716c" style={{ fontSize: '11px' }} />
              <Tooltip contentStyle={{ background: '#fafaf9', border: '1px solid #d6d3d1', fontSize: '12px' }} formatter={(v) => [fmt(v) + ' ramps', 'Count']} />
              <Bar dataKey="count">
                {MATERIAL_DATA.map((m, i) => <Cell key={i} fill={m.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="What VDOT's own data reveals · operator-facing transparency">Data quality flags</SectionHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-stone-50 p-4 border-l-4 border-[#DBC078]">
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Width outliers</div>
            <div className="text-2xl text-[#0a0a0a] font-serif-display">196</div>
            <div className="text-xs text-stone-600 mt-1">Records ≥ 200 in; max 5,005" (data entry errors)</div>
          </div>
          <div className="bg-stone-50 p-4 border-l-4 border-[#DBC078]">
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Width = 0</div>
            <div className="text-2xl text-[#0a0a0a] font-serif-display">{fmt(12077)}</div>
            <div className="text-xs text-stone-600 mt-1">No ramp present; needs install verification</div>
          </div>
          <div className="bg-stone-50 p-4 border-l-4 border-[#7A1F2B]">
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Geometry missing</div>
            <div className="text-2xl text-[#7A1F2B] font-serif-display">{fmt(82438)}</div>
            <div className="text-xs text-stone-600 mt-1">All features geometry-null; geocoding required</div>
          </div>
          <div className="bg-stone-50 p-4 border-l-4 border-[#1F7A8C]">
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Active maintenance</div>
            <div className="text-2xl text-[#1F7A8C] font-serif-display">2026-05-18</div>
            <div className="text-xs text-stone-600 mt-1">Most recent improvement; not stale 2019 data</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="AtlasPath augments VDOT's video-log inventory with calibrated LiDAR measurement">Methodology comparison</SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-stone-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Pill>VDOT 2019 inventory</Pill>
              <Pill>Public</Pill>
            </div>
            <h3 className="text-lg text-[#0a0a0a] mb-2 font-serif-display">Video-log + desktop review</h3>
            <ul className="text-sm text-stone-700 space-y-2">
              <li className="flex gap-2"><span className="text-stone-400">·</span> Statewide coverage (82,438 records)</li>
              <li className="flex gap-2"><span className="text-stone-400">·</span> Width to nearest inch (~±2"–4" est.)</li>
              <li className="flex gap-2"><span className="text-stone-400">·</span> DWS type observable; spec compliance not verifiable</li>
              <li className="flex gap-2"><span className="text-stone-400">·</span> No slope measurement</li>
              <li className="flex gap-2"><span className="text-stone-400">·</span> Material condition: reviewer judgment</li>
            </ul>
            <CitationBlock source="VTRC 21-R18 (March 2021); VDOT IIM-TE-376.1." />
          </div>
          <div className="border border-stone-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Pill variant="teal">AtlasPath</Pill>
              <Pill variant="gold">NIST-traceable</Pill>
            </div>
            <h3 className="text-lg text-[#0a0a0a] mb-2 font-serif-display">Mobile Device Pro LiDAR + Open3D + physical instruments</h3>
            <ul className="text-sm text-stone-700 space-y-2">
              <li className="flex gap-2"><span className="text-[#1F7A8C]">✓</span> Slope: ±0.05 pp with Stabila smart level</li>
              <li className="flex gap-2"><span className="text-[#1F7A8C]">✓</span> Width: ±0.04 in with Bosch GLM 40 laser</li>
              <li className="flex gap-2"><span className="text-[#1F7A8C]">✓</span> Displacement: ±0.001 in with Mitutoyo caliper</li>
              <li className="flex gap-2"><span className="text-[#1F7A8C]">✓</span> DWS dome geometry: ±0.001 in with caliper</li>
              <li className="flex gap-2"><span className="text-[#1F7A8C]">✓</span> Preserved 3D point clouds (5-year audit retention)</li>
            </ul>
            <CitationBlock source="CDOT Curb Ramp Measurement Guide v1.0 (Jan 2020); PROWAG R3; NIST traceability policy." />
          </div>
        </div>
      </div>
    </div>
  );
}
